import {
	IconDevices,
	IconShieldLock,
	IconSpy,
	IconUser,
	IconUsersGroup,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
	STAFF_ROLE_FULL,
	STAFF_ROLE_READ_ONLY,
	useStaffGuard,
} from "@/components/admin/staff-guard";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useAuth } from "@/components/providers/auth";
import { useFetchAdminUserDetail } from "@/components/queries/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { deleteRefreshToken, setAccessToken } from "@/utils/local-storage";

const STAFF_ROLE_NONE = "none";

export function AdminUserDetail() {
	const { isStaff, isFullStaff, isLoading } = useStaffGuard();
	const { user: currentUser } = useAuth();
	const { userId } = Route.useParams();
	const { userDetail, isLoadingUserDetail, refetchUserDetail } =
		useFetchAdminUserDetail(userId);
	const [impersonateOpen, setImpersonateOpen] = useState(false);
	const [isActing, setIsActing] = useState(false);

	if (isLoading || isLoadingUserDetail || !isStaff) {
		return <Loader />;
	}

	const isSelf = currentUser?.id === userDetail?.id;
	const fullName =
		[userDetail?.first_name, userDetail?.last_name].filter(Boolean).join(" ") ||
		"—";

	const revokeSessions = async () => {
		setIsActing(true);
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.adminUsersRevokeSessionsCreate(userId),
			);
			toast.success("Sessions revoked", {
				description: "The user has been signed out of every device.",
			});
			await refetchUserDetail();
		} catch (error) {
			toast.error("Couldn't revoke sessions", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		} finally {
			setIsActing(false);
		}
	};

	const setStaffRole = async (role: string | null) => {
		if (role === null) return;
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.adminUsersStaffRoleUpdate(userId, {
					staff_role: role === STAFF_ROLE_NONE ? "" : role,
				}),
			);
			toast.success("Staff role updated");
			await refetchUserDetail();
		} catch (error) {
			toast.error("Couldn't update the staff role", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const impersonate = async () => {
		setIsActing(true);
		try {
			const response = await requestWithHandling<{
				access_token: string;
				expires_in: number;
				email: string;
			}>(() => apiClient.api.adminUsersImpersonateCreate(userId));
			const token = response.data?.access_token;
			if (!token) {
				throw new Error("No impersonation token returned");
			}
			// Switch identities completely: drop the staff refresh token so the
			// support session ends when the short-lived access token expires.
			deleteRefreshToken();
			setAccessToken(token);
			localStorage.removeItem("teamID");
			toast.success(`Impersonating ${response.data?.email}`, {
				description: "This support session expires in 15 minutes.",
			});
			// Full reload so every provider re-reads the impersonated identity
			window.location.href = "/dashboard";
		} catch (error) {
			toast.error("Couldn't impersonate the user", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
			setIsActing(false);
		}
	};

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Admin", href: "/admin" },
					{ title: "Users", href: "/admin/users" },
					{ title: userDetail?.email ?? "User", href: null },
				]}
				title={userDetail?.email ?? "User"}
				description="Staff view of this user account"
				icon={<IconUser className="size-6 text-primary" aria-hidden />}
			/>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
				<Card className="shadow-sm lg:col-span-2">
					<CardHeader className="pb-4">
						<CardTitle>Profile</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
							<div>
								<dt className="text-muted-foreground">Name</dt>
								<dd className="font-medium">{fullName}</dd>
							</div>
							<div>
								<dt className="text-muted-foreground">Email</dt>
								<dd className="font-medium">{userDetail?.email}</dd>
							</div>
							<div>
								<dt className="text-muted-foreground">Joined</dt>
								<dd className="font-medium">
									{userDetail?.created_at
										? new Date(userDetail.created_at).toLocaleString()
										: "—"}
								</dd>
							</div>
							<div>
								<dt className="text-muted-foreground">Active sessions</dt>
								<dd className="font-medium">
									{userDetail?.active_sessions ?? 0}
								</dd>
							</div>
							<div className="md:col-span-2">
								<dt className="text-muted-foreground">Status</dt>
								<dd className="flex gap-1 flex-wrap mt-1">
									{userDetail?.is_verified ? (
										<Badge variant="secondary">Verified</Badge>
									) : (
										<Badge variant="outline">Unverified</Badge>
									)}
									{userDetail?.mfa_enabled && (
										<Badge variant="secondary">MFA enabled</Badge>
									)}
									{userDetail?.staff_role && (
										<Badge>{userDetail.staff_role}</Badge>
									)}
								</dd>
							</div>
						</dl>
					</CardContent>
				</Card>

				<Card className="shadow-sm border-orange-500/30">
					<CardHeader className="pb-4">
						<CardTitle className="flex items-center gap-2">
							<IconShieldLock className="h-5 w-5" aria-hidden />
							Staff Actions
						</CardTitle>
						<CardDescription>
							{isFullStaff
								? "Audit-logged administrative actions"
								: "Read-only staff cannot perform actions"}
						</CardDescription>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						<Button
							variant="outline"
							className="w-full justify-start"
							disabled={!isFullStaff || isActing}
							onClick={revokeSessions}
						>
							<IconDevices className="size-4 mr-2" aria-hidden />
							Sign out all sessions
						</Button>
						<Button
							variant="outline"
							className="w-full justify-start"
							disabled={!isFullStaff || isActing || !!userDetail?.staff_role}
							onClick={() => setImpersonateOpen(true)}
						>
							<IconSpy className="size-4 mr-2" aria-hidden />
							Impersonate user
						</Button>
						<div className="space-y-1.5 pt-1">
							<p className="text-sm font-medium">Staff role</p>
							<Select
								value={userDetail?.staff_role || STAFF_ROLE_NONE}
								onValueChange={setStaffRole}
								disabled={!isFullStaff || isSelf}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="No staff access" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={STAFF_ROLE_NONE}>
										No staff access
									</SelectItem>
									<SelectItem value={STAFF_ROLE_READ_ONLY}>
										Staff (read-only)
									</SelectItem>
									<SelectItem value={STAFF_ROLE_FULL}>
										Staff (full access)
									</SelectItem>
								</SelectContent>
							</Select>
							{isSelf && (
								<p className="text-xs text-muted-foreground">
									You cannot change your own staff role.
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			</div>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center gap-2">
						<IconUsersGroup className="h-5 w-5" aria-hidden />
						Teams
					</CardTitle>
					<CardDescription>Memberships and plans</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Team</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Role</TableHead>
								<TableHead>Plan</TableHead>
								<TableHead>Joined</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(userDetail?.teams ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={5}
										className="text-center text-muted-foreground py-8"
									>
										No team memberships.
									</TableCell>
								</TableRow>
							)}
							{(userDetail?.teams ?? []).map((team) => (
								<TableRow key={team.team_id}>
									<TableCell>
										<Link
											to="/admin/teams/$teamId"
											params={{ teamId: team.team_id ?? "" }}
											className="font-medium hover:underline"
										>
											{team.name}
										</Link>
									</TableCell>
									<TableCell className="capitalize">{team.type}</TableCell>
									<TableCell>{team.role}</TableCell>
									<TableCell>{team.plan_name || "—"}</TableCell>
									<TableCell className="text-muted-foreground">
										{team.joined_at
											? new Date(team.joined_at).toLocaleDateString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle>Recent Activity</CardTitle>
					<CardDescription>Last 20 audit log events</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Action</TableHead>
								<TableHead>IP Address</TableHead>
								<TableHead>When</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(userDetail?.recent_audit_logs ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={3}
										className="text-center text-muted-foreground py-8"
									>
										No recent activity.
									</TableCell>
								</TableRow>
							)}
							{(userDetail?.recent_audit_logs ?? []).map((log) => (
								<TableRow key={log.id}>
									<TableCell className="font-medium">{log.action}</TableCell>
									<TableCell>{log.ip_address}</TableCell>
									<TableCell className="text-muted-foreground">
										{log.created_at
											? new Date(log.created_at).toLocaleString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Impersonate {userDetail?.email}?</DialogTitle>
						<DialogDescription>
							You'll switch to this user's account for up to 15 minutes to debug
							their experience. Your staff session ends — you'll need to sign in
							again afterwards. The user's audit log records this impersonation.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setImpersonateOpen(false)}>
							Cancel
						</Button>
						<Button onClick={impersonate} disabled={isActing}>
							{isActing ? "Switching…" : "Impersonate"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/admin/users/$userId")({
	component: AdminUserDetail,
});
/* istanbul ignore end */
